<template>
    <div class="automation-button-wrapper">
        <div class="automation-trigger-button" @click="toggleAutomationList">
            <AutomationIcon class="automation-icon" />
            <span v-if="showText" class="button-text">{{ $t("message.automations") }}</span>
        </div>

        <!-- Custom Dropdown -->
        <div v-if="visible" class="custom-dropdown" @click.stop>
            <div class="dropdown-content" :class="{ 'inline-mode': subMenuMode === 'inline' }">
                <!-- Overlay mode: two-level menu -->
                <template v-if="subMenuMode === 'overlay'">
                    <!-- Categories with automations -->
                    <div
                        v-for="category in categoriesWithAutomations"
                        :key="category.name"
                        class="automation-item category-item"
                        @mouseenter="handleCategoryHover($event, category.name)"
                        @mouseleave="handleCategoryLeave"
                    >
                        <i v-if="category.icon" class="material-icons category-icon-small">{{ category.icon }}</i>
                        <i v-else class="material-icons category-icon-small">folder</i>
                        <span class="automation-name">{{ category.name }}</span>
                        <i class="material-icons submenu-arrow">chevron_right</i>

                        <!-- Submenu -->
                        <div
                            class="submenu-dropdown"
                            :style="hoveredCategory === category.name ? submenuPosition : {}"
                            @mouseenter="cancelCategoryLeave"
                            @mouseleave="handleCategoryLeave"
                        >
                            <div class="submenu-inner">
                                <div
                                    v-for="automation in category.automations"
                                    :key="automation._id"
                                    class="automation-item"
                                    @click.stop="selectAutomation(automation)"
                                    v-tippy
                                    :content="automation.name"
                                    data-tippy-placement="bottom"
                                    data-tippy-theme="light-border"
                                >
                                    <div class="automation-icon-wrapper">
                                        <AutomationIcon class="list-icon" />
                                    </div>
                                    <span class="automation-name">{{ automation.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Divider when both categories and general automations exist -->
                    <div
                        v-if="categoriesWithAutomations.length > 0 && generalAutomations.length > 0"
                        class="divider"
                    ></div>

                    <!-- General (uncategorized) automations -->
                    <div
                        v-for="automation in generalAutomations"
                        :key="automation._id"
                        class="automation-item"
                        @click="selectAutomation(automation)"
                        v-tippy
                        :content="automation.name"
                        data-tippy-placement="bottom"
                        data-tippy-theme="light-border"
                    >
                        <div class="automation-icon-wrapper">
                            <AutomationIcon class="list-icon" />
                        </div>
                        <span class="automation-name">{{ automation.name }}</span>
                    </div>
                </template>

                <!-- Inline mode: flat grouped list -->
                <template v-else>
                    <div v-for="(category, index) in allCategoriesForInline" :key="category.name">
                        <!-- Category header -->
                        <div class="category-header">
                            <i v-if="category.icon" class="material-icons category-icon-small">{{ category.icon }}</i>
                            <i v-else class="material-icons category-icon-small">folder</i>
                            <span class="category-title">{{ category.name }}</span>
                        </div>

                        <!-- Automations under category -->
                        <div
                            v-for="automation in category.automations"
                            :key="automation._id"
                            class="automation-item"
                            @click="selectAutomation(automation)"
                            v-tippy
                            :content="automation.name"
                            data-tippy-placement="bottom"
                            data-tippy-theme="light-border"
                        >
                            <div class="automation-icon-wrapper">
                                <AutomationIcon class="list-icon" />
                            </div>
                            <span class="automation-name">{{ automation.name }}</span>
                        </div>

                        <!-- Divider between categories -->
                        <div v-if="index < allCategoriesForInline.length - 1" class="divider"></div>
                    </div>
                </template>
            </div>
            <div class="divider"></div>
            <div class="automation-item create-new" @click="showCreateAutomationModal">
                <div class="plus-icon-wrapper">
                    <span class="plus-icon">+</span>
                </div>
                <span class="automation-name">{{ $t("message.newAutomation") }}</span>
            </div>
        </div>

        <!-- Backdrop -->
        <div v-if="visible" class="dropdown-backdrop" @click="visible = false"></div>

        <Modal2
            v-if="createAutomationModalVisible"
            :title="$t('message.createProfileAutomation')"
            :show-footer="false"
            @close="createAutomationModalVisible = false"
            @cancel="createAutomationModalVisible = false"
        >
            <a-alert
                type="info"
                show-icon
                :message="$t('message.profileButtonClicked')"
                :description="$t('message.profileButtonDescription')"
                class="mb-4"
            />
            <a-input v-model="newAutomationName" :placeholder="$t('message.automationName')" />
            <div class="mt-4 text-right">
                <a-button @click="createAutomationModalVisible = false" class="mr-2">{{
                    $t("message.cancel")
                }}</a-button>
                <a-button type="primary" @click="createAutomation">{{ $t("message.create") }}</a-button>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { AutomationEventTypes } from "../../views/Pages/Automation/utils/AutomationModuleTypes";
import { sendSocketMessage } from "../../utils/socketStream";
import Modal2 from "./Modal2.vue";
import { AUTOMATION_API } from "../../api/automation";
import { sessionId } from "../../utils/session";
import AutomationIcon from "../../components/svg/icons/AutomationIcon.vue";

export default {
    name: "ContactProfileAutomationButton",
    components: {
        Modal2,
        AutomationIcon,
    },
    props: {
        contactId: {
            type: String,
            required: true,
        },
        showText: {
            type: Boolean,
            default: true,
        },
        subMenuMode: {
            type: String,
            default: "overlay", // 'overlay' veya 'inline'
            validator: (value) => ["overlay", "inline"].includes(value),
        },
    },
    data() {
        return {
            visible: false,
            createAutomationModalVisible: false,
            newAutomationName: "",
            hoveredCategory: null,
            submenuPosition: {},
            categoryLeaveTimeout: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            allAutomations: (state) => state.automation.automations,
        }),
        automations() {
            const currentUserId = this.user?._id;
            const userDepartments = this.user?.subjects || [];

            return this.allAutomations.filter((x) => {
                if (
                    x.options?.trigger !== "event" ||
                    x.options?.event?.type !== AutomationEventTypes.CONTACT.PROFILE_BUTTON_CLICKED ||
                    !x.published
                ) {
                    return false;
                }

                // Eğer executionPrivacy tanımlıysa ve kullanıcı/departman kısıtlaması varsa kontrol et
                if (x.executionPrivacy) {
                    const allowedUsers = x.executionPrivacy.users || [];
                    const allowedDepartments = x.executionPrivacy.departments || [];

                    // Eğer hem users hem de departments boşsa, herkese açık demektir
                    if (allowedUsers.length === 0 && allowedDepartments.length === 0) {
                        return true;
                    }

                    // Kullanıcı izin listesinde mi?
                    const hasUserPermission = allowedUsers.some((userId) => userId === currentUserId);

                    // Kullanıcının departmanı izin listesinde mi?
                    const hasDepartmentPermission =
                        allowedDepartments.some((deptId) => userDepartments.includes(deptId)) ||
                        userDepartments.length === 0;

                    return hasUserPermission || hasDepartmentPermission;
                }

                return true;
            });
        },
        generalAutomations() {
            return this.automations.filter((x) => !x.category || !x.category.name || x.category.name === "Genel");
        },
        categoriesWithAutomations() {
            const categories = {};

            this.automations.forEach((automation) => {
                const categoryName = automation.category?.name;
                if (categoryName && categoryName !== "Genel") {
                    if (!categories[categoryName]) {
                        categories[categoryName] = {
                            name: categoryName,
                            icon: automation.category?.icon,
                            automations: [],
                        };
                    }
                    categories[categoryName].automations.push(automation);
                }
            });

            return Object.values(categories).sort((a, b) => a.name.localeCompare(b.name));
        },
        allCategoriesForInline() {
            // Inline mode için tüm kategorileri (Genel dahil) döndür
            const categories = {};

            this.automations.forEach((automation) => {
                const categoryName = automation.category?.name || "Genel";
                if (!categories[categoryName]) {
                    categories[categoryName] = {
                        name: categoryName,
                        icon: automation.category?.icon,
                        automations: [],
                    };
                }
                categories[categoryName].automations.push(automation);
            });

            // Kategorileri sırala - Genel en sonda olacak şekilde
            const categoriesArray = Object.values(categories);
            const generalCategory = categoriesArray.find((c) => c.name === "Genel");
            const otherCategories = categoriesArray
                .filter((c) => c.name !== "Genel")
                .sort((a, b) => a.name.localeCompare(b.name));

            return generalCategory ? [...otherCategories, generalCategory] : otherCategories;
        },
    },
    methods: {
        ...mapActions({
            syncAutomations: "automation/syncAutomations",
        }),
        toggleAutomationList() {
            this.visible = !this.visible;
        },
        selectAutomation(automation) {
            this.visible = false;
            sendSocketMessage("automation run-profile-button", {
                automationId: automation._id,
                siteId: this.$store.state.site.site._id,
                userId: this.$store.state.auth.user._id,
                contactId: this.contactId,
                sessionId: sessionId,
            });
        },
        showCreateAutomationModal() {
            this.createAutomationModalVisible = true;
            this.newAutomationName = this.$t("message.untitledAutomation");
            this.visible = false;
        },
        gotoAutomation(automation) {
            this.$router.push(`/automations/${automation._id}/design`);
        },
        handleCategoryHover(event, categoryName) {
            this.cancelCategoryLeave();
            this.hoveredCategory = categoryName;
            const rect = event.currentTarget.getBoundingClientRect();
            this.submenuPosition = {
                top: `${rect.top}px`,
                left: `${rect.right}px`,
                paddingLeft: "8px",
            };
        },
        handleCategoryLeave() {
            this.categoryLeaveTimeout = setTimeout(() => {
                this.hoveredCategory = null;
            }, 100);
        },
        cancelCategoryLeave() {
            if (this.categoryLeaveTimeout) {
                clearTimeout(this.categoryLeaveTimeout);
                this.categoryLeaveTimeout = null;
            }
        },
        async createAutomation() {
            const name = this.newAutomationName;
            const event = AutomationEventTypes.CONTACT.PROFILE_BUTTON_CLICKED;
            const payload = { contactId: this.contactId };

            try {
                const automation = await AUTOMATION_API.createEmptyEventAutomation(name, event, payload);
                this.$message.success("Automation created successfully");
                this.createAutomationModalVisible = false;
                this.newAutomationName = "";
                this.syncAutomations();
                setTimeout(() => {
                    this.gotoAutomation(automation);
                }, 1000);
            } catch (error) {
                this.$message.error(this.$t("automation.automationCouldNotBeCreated"));
                console.error("Error while creating automation", error);
            }
        },
    },
    created() {
        if (this.automations.length === 0) {
            this.syncAutomations();
        }
    },
};
</script>

<style lang="scss" scoped>
.automation-button-wrapper {
    position: relative;
    display: inline-block;
}

.automation-trigger-button {
    width: 35px;
    height: 35px;
    border-radius: 9px;
    background: #f2edff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    padding: 0;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    &:active {
        transform: translateY(0);
    }

    .automation-icon {
        width: 20px;
        height: 20px;
        fill: #7c5cdb;
    }

    .button-text {
        color: #7c5cdb;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
    }

    &:has(.button-text) {
        width: auto;
        padding: 0 16px;
    }
}

.custom-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 1055;
    min-width: 177px;
    max-width: 250px;
    width: max-content;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .dropdown-content {
        max-height: 220px;
        overflow-y: auto;
        padding: 4px 4px 0 4px;
        border-radius: 12px;

        &::-webkit-scrollbar {
            width: 3px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: #c9bbf6;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #b8a5e8;
        }

        // Inline mode için özel stiller
        &.inline-mode {
            max-height: 400px;

            .category-header {
                display: flex;
                align-items: center;
                gap: 7px;
                padding: 7px;
                background: #f5f3ff;
                border-radius: 4px;
                margin-bottom: 2px;
                font-weight: 600;
                font-size: 11px;
                color: #7c5cdb;
                text-transform: uppercase;
                letter-spacing: 0.5px;

                .category-icon-small {
                    font-size: 16px;
                    color: #7c5cdb;
                }

                .category-title {
                    flex: 1;
                }
            }
        }
    }

    .automation-item {
        display: flex;
        align-items: center;
        gap: 7px;
        padding: 7px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        margin-bottom: 2px;

        &:last-child {
            margin-bottom: 0;
        }

        &:hover {
            background: #f5f3ff;
        }

        .automation-icon-wrapper {
            width: 18px;
            height: 18px;
            min-width: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            .list-icon {
                width: 18px;
                height: 18px;
                fill: #7c5cdb;
            }
        }

        .automation-name {
            color: #1a1d1f;
            font-size: 12px;
            font-weight: 500;
            font-family: Inter, sans-serif;
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &.category-item {
            position: relative;

            .category-icon-small {
                font-size: 18px;
                color: #7c5cdb;
                min-width: 18px;
            }

            .submenu-arrow {
                font-size: 16px;
                color: #7c5cdb;
                margin-left: auto;
            }

            .submenu-dropdown {
                display: none;
                position: fixed;
                z-index: 1060;

                .submenu-inner {
                    min-width: 177px;
                    max-width: 202px;
                    padding: 4px;
                    background: #ffffff;
                    border-radius: 12px;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

                    .automation-item {
                        margin-bottom: 2px;

                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }

            &:hover .submenu-dropdown {
                display: block;
            }
        }

        &.create-new {
            margin: 0 4px 4px 4px;
            justify-content: center;

            .plus-icon-wrapper {
                width: 18px;
                height: 18px;
                min-width: 18px;
                border-radius: 4px;
                background: #f5f3ff;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                .plus-icon {
                    font-size: 15px;
                    color: #7c5cdb;
                    font-weight: 500;
                    line-height: 1;
                }
            }

            .automation-name {
                color: #7c5cdb;
            }

            &:hover {
                background: #f5f3ff;

                .plus-icon-wrapper {
                    background: #ebe5ff;
                }
            }
        }
    }

    .divider {
        height: 1px;
        background: #e8e8ea;
        margin: 4px 0;
        flex-shrink: 0;
    }
}

.dropdown-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1054;
    background: transparent;
}
</style>
